/**
 * @file receipt-expiry.ts
 * @description Срок годности строки приёмки «мягко» (phase19.22, W20;
 *   ui-design §7): политика срока по виду товара, автосчёт от даты
 *   изготовления, подсказки «Рассчитано…» и «По карточке выходит…».
 *
 * Зачем файл существует: сервер считает срок тем же правилом
 * (`receipt-rules.ts`: `pickShelfLife` + `computeExpiry` в UTC-сутках), и
 * подсказка в форме обязана совпасть с тем, что сервер запишет в партию.
 * Разойдутся — кладовщик увидит «15.03.2027», а в партии окажется 16.03.
 * Выдумывать срок нельзя (ТР ТС 022): нет даты изготовления или дней — пусто.
 */

import { pluralRu } from "./plural";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Средняя длина месяца для подписи «≈ N мес.» (365.25 / 12). */
const AVG_MONTH_DAYS = 30.4375;

/** Откуда взялось число дней: карточка товара или вид товара. */
export type ShelfLifeSource = "product" | "kind";

/**
 * Политика срока фасовки. `requiresExpiry: null` — не удалось узнать (нет
 * прав на каталог, сбой сети): поля показываем, автосчёт не делаем, срок
 * посчитает сервер.
 */
export interface ExpiryPolicy {
  requiresExpiry: boolean | null;
  shelfLifeDays: number | null;
  source: ShelfLifeSource | null;
  /** Название действующего вида: «Срок годности не ведётся (вид: Непищевой)». */
  kindName: string | null;
}

/** Политика неизвестна — поля видны, расчёта нет. */
export const UNKNOWN_EXPIRY_POLICY: ExpiryPolicy = {
  requiresExpiry: null,
  shelfLifeDays: null,
  source: null,
  kindName: null,
};

/** Действующий вид товара — поля ответа каталога (`EffectiveKindDto`). */
export interface EffectiveKindLike {
  name: string;
  requiresExpiry: boolean;
  defaultShelfLifeDays: number | null;
}

/** Зеркало серверного `pickShelfLife`: карточка товара, иначе вид товара. */
export function resolveExpiryPolicy(
  productShelfLifeDays: number | null | undefined,
  kind: EffectiveKindLike | null | undefined,
): ExpiryPolicy {
  if (kind && !kind.requiresExpiry) {
    return { requiresExpiry: false, shelfLifeDays: null, source: null, kindName: kind.name };
  }
  const kindName = kind?.name ?? null;
  // Вида нет (до сопоставления видов) — срок считаем обязательным «мягко».
  const requiresExpiry = true;
  if (productShelfLifeDays) {
    return { requiresExpiry, shelfLifeDays: productShelfLifeDays, source: "product", kindName };
  }
  if (kind?.defaultShelfLifeDays) {
    return { requiresExpiry, shelfLifeDays: kind.defaultShelfLifeDays, source: "kind", kindName };
  }
  return { requiresExpiry, shelfLifeDays: null, source: null, kindName };
}

/** Дата изготовления + дни в UTC-сутках (как `computeExpiry` сервера). */
export function computeExpiry(manufacturedAt: string, days: number | null): string | null {
  if (!manufacturedAt || !days) return null;
  const base = new Date(`${manufacturedAt}T00:00:00.000Z`);
  if (Number.isNaN(base.getTime())) return null;
  return new Date(base.getTime() + days * DAY_MS).toISOString().slice(0, 10);
}

/** Даты строки, которые пересчитывает автосрок. */
export interface ExpiryDates {
  manufacturedAt: string;
  expiresAt: string;
  /** Срок вписан руками — автосчёт его не трогает. */
  expiresManual: boolean;
}

/**
 * Пересчитать автосрок (W20): срок, не вписанный руками, всегда равен «дата
 * изготовления + дни» ТЕКУЩЕЙ фасовки — сменили фасовку или стёрли дату —
 * срок пересчитан или очищен. Ручной срок не трогаем никогда.
 */
export function withAutoExpiry<T extends ExpiryDates>(lot: T, shelfLifeDays: number | null): T {
  if (lot.expiresManual) return lot;
  const auto = computeExpiry(lot.manufacturedAt, shelfLifeDays) ?? "";
  return auto === lot.expiresAt ? lot : { ...lot, expiresAt: auto };
}

/** «≈ 12 мес.» — показ дней в месяцах (W20: хранение в днях). */
export function approxMonths(days: number): number {
  return Math.max(1, Math.round(days / AVG_MONTH_DAYS));
}

/** `YYYY-MM-DD` → «15.03.2027». */
export function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

/** Подпись источника дней. */
const SOURCE_LABEL: Record<ShelfLifeSource, string> = {
  product: "из карточки",
  kind: "из вида товара",
};

/** «Рассчитано: 15.03.2026 + 365 дн. (≈ 12 мес., из карточки)». */
export function calculatedHint(
  manufacturedAt: string,
  days: number,
  source: ShelfLifeSource | null,
): string {
  const tail = source ? `, ${SOURCE_LABEL[source]}` : "";
  return `Рассчитано: ${formatIsoDate(manufacturedAt)} + ${days} дн. (≈ ${approxMonths(days)} мес.${tail})`;
}

/**
 * Ручной срок расходится с расчётом — подсказка «По карточке выходит …»
 * (не блок, W20). `null` — расхождения нет или считать не из чего.
 */
export function mismatchHint(expiresAt: string, calculated: string | null): string | null {
  if (!expiresAt || !calculated || expiresAt === calculated) return null;
  return `По карточке выходит ${formatIsoDate(calculated)} — проверьте`;
}

/** «3 позиции без срока годности» — заголовок подтверждения проведения. */
export function missingExpiryTitle(count: number): string {
  const word = pluralRu(count, ["позиция", "позиции", "позиций"]);
  return `${count} ${word} без срока годности`;
}

/** «до 03.2027» — месяц и год срока для коротких подписей. */
export function formatMonthYear(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m] = iso.slice(0, 10).split("-");
  return y && m ? `${m}.${y}` : "—";
}
